import React, { useRef, useState } from 'react';
import { useForegroundTiming, useLearningTimingPause } from '../../hooks/useForegroundTiming';
import { useEditorStore } from '../../store/editorStore';
import styled from 'styled-components';
import { MethodSelectorPanel } from './MethodSelectorPanel';
import { AIReportPanel } from './AIReportPanel';
import { AIHelpModal } from './AIHelpModal';
import glassTheme from '../../styles/glassTheme';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  min-height: 0;
  ${glassTheme.glass.light}
  border-radius: ${glassTheme.borderRadius.lg};
  overflow: hidden;
`;

const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  min-height: 56px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid ${glassTheme.colors.border};
  gap: ${glassTheme.spacing.sm};

  @media (max-width: 1024px) {
    flex-wrap: wrap;
  }
`;

const EditorTitle = styled.span`
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EditorActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EditorButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  background: ${props => {
    switch (props.variant) {
      case 'primary':
        return glassTheme.gradients.cyber;
      case 'danger':
        return 'transparent';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border: 1px solid ${props => props.variant === 'danger' ? glassTheme.colors.status.error : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  transition: all ${glassTheme.transition.base};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
  }

  @media (max-width: 480px) {
    flex: 1;
    justify-content: center;
  }
`;

const EditorWrapper = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(96, 165, 250, 0.5);
    border-radius: 6px;
    border: 2px solid rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(96, 165, 250, 0.7);
    }
  }

  &::-webkit-scrollbar-corner {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const TextEditor = styled.textarea`
  width: 100%;
  min-height: 100%;
  height: auto;
  background: #1e1e1e;
  color: #d4d4d4;
  border: none;
  padding: 1rem;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  tab-size: 4;
  overflow: auto;
  white-space: pre;
  overflow-wrap: normal;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const LineNumbers = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 50px;
  height: 100%;
  background: rgba(20, 20, 20, 0.9);
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 1rem 0.5rem 1rem 1rem;
  text-align: right;
  user-select: none;
  overflow: hidden;
  pointer-events: none;
  white-space: pre;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
`;

const LineNumbersWithRef = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  (props, ref) => <LineNumbers ref={ref}>{props.children}</LineNumbers>
);

const EditorContent = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  position: relative;
`;

const EditorTextArea = styled(TextEditor)`
  padding-left: 60px;
`;

const Spinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 14px;
  height: 14px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface MonacoEditorProps {
  height?: string;
  onRunCode?: () => void;
  onAskAI?: () => void;
}

const EXAMPLE_CODES = {
  basic: `# 基础统计分析
# 生成随机数据
set.seed(123)
x <- rnorm(100, mean=50, sd=10)

# 计算描述性统计
summary(x)
var(x)
sd(x)

# 置信区间
t.test(x)$conf.int`,

  plot: `# 数据可视化
# 生成数据
x <- 1:10
y <- x^2 + rnorm(10, 0, 5)

# 散点图
plot(x, y, main="散点图示例",
     xlab="X轴", ylab="Y轴", pch=19, col="blue")

# 添加趋势线
abline(lm(y ~ x), col="red", lwd=2)`,

  regression: `# 线性回归分析
# 生成数据
set.seed(456)
x1 <- rnorm(50)
x2 <- rnorm(50)
y <- 2 + 3*x1 - 1.5*x2 + rnorm(50, 0, 0.5)

# 建立模型
model <- lm(y ~ x1 + x2)

# 查看结果
summary(model)

# 残差分析
plot(model)`,

  ttest: `# t检验示例
# 生成两组数据
group1 <- rnorm(30, mean=10, sd=2)
group2 <- rnorm(30, mean=12, sd=2)

# 独立样本t检验
t.test(group1, group2)

# 配对样本t检验
before <- rnorm(20, mean=50, sd=5)
after <- before + rnorm(20, mean=5, sd=2)
t.test(before, after, paired=TRUE)`
};


const generateLineNumbers = (code: string): string => {
  const lines = code.split('\n');
  return lines.map((_, index) => index + 1).join('\n');
};

/**
 * Detects if a line contains an incomplete R statement
 * Returns true if the line is incomplete (requires continuation)
 */
const isIncompleteRStatement = (line: string): boolean => {
  const trimmed = line.trim();

  // Empty lines are not incomplete
  if (!trimmed) return false;

  // Comments are complete
  if (trimmed.startsWith('#')) return false;

  // Check for operators that suggest continuation
  const continuationOperators = ['+', '-', '|', '&', '\\', '^', '~', ',', '\\$'];
  if (continuationOperators.some(op => trimmed.endsWith(op))) {
    return true;
  }

  // Check for opening brackets/parentheses without closing
  const openParens = (line.match(/\(/g) || []).length;
  const closeParens = (line.match(/\)/g) || []).length;
  const openBrackets = (line.match(/\[/g) || []).length;
  const closeBrackets = (line.match(/\]/g) || []).length;
  const openBraces = (line.match(/\{/g) || []).length;
  const closeBraces = (line.match(/\}/g) || []).length;

  if (openParens > closeParens) return true;
  if (openBrackets > closeBrackets) return true;
  if (openBraces > closeBraces) return true;

  // Check for incomplete control structures
  const incompleteControlPatterns = [
    /\bif\s*\([^)]*\)\s*$/,               // if (...) without brace
    /\bif\s*\([^)]*\)\s*\{$/,              // if (...) { without closing
    /\belse\s*$/,                          // else without anything
    /\belse\s*\{$/,                        // else { without closing
    /\bfor\s*\([^)]*\)\s*$/,               // for (...) without brace
    /\bfor\s*\([^)]*\)\s*\{$/,              // for (...) { without closing
    /\bwhile\s*\([^)]*\)\s*$/,             // while (...) without brace
    /\bwhile\s*\([^)]*\)\s*\{$/,            // while (...) { without closing
    /\bfunction\s*\([^)]*\)\s*$/,          // function(...) without brace
    /\bfunction\s*\([^)]*\)\s*\{$/,         // function(...) { without closing
  ];

  if (incompleteControlPatterns.some(pattern => pattern.test(trimmed))) {
    return true;
  }

  return false;
};

/**
 * Collects a complete R statement starting from the given line index
 * Returns the statement text and the end line index
 */
const collectCompleteStatement = (
  lines: string[],
  startLineIndex: number
): { statement: string; endIndex: number } => {
  let statementLines: string[] = [];
  let currentIndex = startLineIndex;

  // Find first non-empty, non-comment line
  while (currentIndex < lines.length) {
    const trimmedLine = lines[currentIndex].trim();
    if (trimmedLine && !trimmedLine.startsWith('#')) {
      break;
    }
    currentIndex++;
  }

  if (currentIndex >= lines.length) {
    return { statement: '', endIndex: startLineIndex };
  }

  // Collect lines until we have a complete statement
  statementLines.push(lines[currentIndex]);
  currentIndex++;

  while (currentIndex < lines.length) {
    const currentLine = lines[currentIndex];
    const trimmedLine = currentLine.trim();

    // If we hit an empty line or comment, check if current statement is complete
    if (!trimmedLine || trimmedLine.startsWith('#')) {
      // Check if the accumulated statement is complete
      const testStatement = statementLines.join('\n');
      if (!isIncompleteRStatement(testStatement)) {
        break;
      }
      currentIndex++;
      continue;
    }

    // Check if the previous line was incomplete
    const accumulatedSoFar = statementLines.join('\n');
    if (!isIncompleteRStatement(accumulatedSoFar)) {
      // Previous statement is complete, don't include this line
      break;
    }

    // Add this line to the statement
    statementLines.push(currentLine);
    currentIndex++;
  }

  return {
    statement: statementLines.join('\n'),
    endIndex: currentIndex - 1
  };
};

export const MonacoEditorComponent: React.FC<MonacoEditorProps> = ({
  height = '100%',
  onRunCode,
  onAskAI
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const {
    code,
    setCode,
    runSelection,
    isRunning
  } = useEditorStore();
  const [hasSelection, setHasSelection] = useState(false);
  const [isMethodSelectorOpen, setIsMethodSelectorOpen] = useState(false);
  const [aiReport, setAiReport] = useState('');
  const [isReportVisible, setIsReportVisible] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [isReportStreaming, setIsReportStreaming] = useState(false);
  const [isAIHelpOpen, setIsAIHelpOpen] = useState(false);
  useLearningTimingPause(isReportVisible && isReportLoading && !aiReport);
  useForegroundTiming(isReportVisible && !!aiReport ? {context: 'platform', platformKey: 'workspace', surface: 'aiHelp'}
    : isMethodSelectorOpen ? {context: 'platform', platformKey: 'workspace', surface: 'demonstration'} : null,
    !isReportLoading || !!aiReport, null, false, 20);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCode(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab键处理
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue = value.substring(0, start) + '    ' + value.substring(end);
      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + 4;
      setCode(newValue);
    }

    // Ctrl+Enter 智能运行：选中代码 > 当前行 > 全部代码
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleRunCode();
    }

    // Shift+Enter 运行当前行并移动到下一行
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      handleRunCode();
    }
  };

  const handleSelectionChange = () => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      setHasSelection(start !== end);
    }
  };

  const moveToNextExecutableLine = (lines: string[], startLineIndex: number) => {
    let nextLineIndex = startLineIndex + 1;
    while (nextLineIndex < lines.length) {
      const trimmedLine = lines[nextLineIndex].trim();
      if (trimmedLine && !trimmedLine.startsWith('#')) {
        // Found next executable line
        let nextLineStart = 0;
        for (let i = 0; i < nextLineIndex; i++) {
          nextLineStart += lines[i].length + 1;
        }

        // Move cursor and add visual feedback
        if (textareaRef.current) {
          textareaRef.current.selectionStart = nextLineStart;
          textareaRef.current.selectionEnd = nextLineStart;
          textareaRef.current.focus();

          // Add visual flicker effect
          textareaRef.current.style.backgroundColor = 'rgba(102, 126, 234, 0.2)';
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.style.backgroundColor = '';
            }
          }, 200);
        }
        return nextLineIndex;
      }
      nextLineIndex++;
    }
    return -1; // No more executable lines
  };

  const handleRunCode = () => {
    // Smart run logic: selection > complete multi-line statement > current line
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;

      // If there's a selection, run the selected code
      if (start !== end) {
        const selectedCode = code.substring(start, end);
        runSelection(selectedCode);

        // After running selection, move cursor to next executable line
        const lines = code.split('\n');
        let currentLineIndex = 0;
        let charCount = 0;

        // Find which line selection ends on
        for (let i = 0; i < lines.length; i++) {
          const lineLength = lines[i].length + 1;
          if (charCount + lineLength > end) {
            currentLineIndex = i;
            break;
          }
          charCount += lineLength;
        }

        moveToNextExecutableLine(lines, currentLineIndex);
        return;
      }

      // If no selection, find current line
      const cursorPos = textareaRef.current.selectionStart;
      const lines = code.split('\n');
      let currentLineIndex = 0;
      let charCount = 0;

      // Find current line index
      for (let i = 0; i < lines.length; i++) {
        const lineLength = lines[i].length + 1;
        if (charCount + lineLength > cursorPos) {
          currentLineIndex = i;
          break;
        }
        charCount += lineLength;
      }

      const currentLineCode = lines[currentLineIndex].trim();

      // RStudio behavior: if current line is empty/comment, skip to next executable line
      let lineToRun = currentLineIndex;
      if (!currentLineCode || currentLineCode.startsWith('#')) {
        // Skip empty lines and comments
        while (lineToRun < lines.length) {
          const trimmedLine = lines[lineToRun].trim();
          if (trimmedLine && !trimmedLine.startsWith('#')) {
            break;
          }
          lineToRun++;
        }

        // Move cursor to the executable line we found
        if (lineToRun < lines.length && lineToRun !== currentLineIndex) {
          let lineStart = 0;
          for (let i = 0; i < lineToRun; i++) {
            lineStart += lines[i].length + 1;
          }
          textareaRef.current.selectionStart = lineStart;
          textareaRef.current.selectionEnd = lineStart;
          textareaRef.current.focus();
        }
      }

      // Check if we have an executable line to run
      if (lineToRun < lines.length) {
        // Collect complete statement (handles multi-line statements)
        const { statement: codeToRun, endIndex } = collectCompleteStatement(lines, lineToRun);

        if (codeToRun && !codeToRun.trim().startsWith('#')) {
          runSelection(codeToRun);
          // Move to next executable line after running the complete statement
          moveToNextExecutableLine(lines, endIndex);
        }
      }
    }
  };

  const handleClearEditor = () => {
    setCode('');
  };

  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const lineNumbers = generateLineNumbers(code);

  return (
    <EditorContainer style={{ height }}>
      <EditorHeader>
        <EditorTitle>
          <i className="fas fa-code"></i>
          R代码编辑器
        </EditorTitle>
        <EditorActions>
          <EditorButton
            variant="primary"
            onClick={handleRunCode}
            disabled={isRunning || !code.trim()}
            aria-describedby="run-code-hint"
            title="运行选中代码或当前行 (Ctrl+Enter 或 Shift+Enter)"
          >
            {isRunning ? (
              <>
                <Spinner />
                运行中...
              </>
            ) : (
              <>
                <i className="fas fa-play"></i>
                运行
              </>
            )}
          </EditorButton>
          <EditorButton onClick={() => setIsAIHelpOpen(true)}>
            <i className="fas fa-lightbulb"></i>
            代码解释
          </EditorButton>
          <span id="run-code-hint" hidden>
            {code.trim() ? '可按 Ctrl+Enter 运行代码' : '请输入要执行的R代码'}
          </span>
          <EditorButton variant="danger" onClick={handleClearEditor} disabled={isRunning}>
            <i className="fas fa-trash"></i>
            清空
          </EditorButton>
        </EditorActions>
      </EditorHeader>
      <EditorWrapper>
        <EditorContent>
          <LineNumbersWithRef ref={lineNumbersRef}>{lineNumbers}</LineNumbersWithRef>
          <EditorTextArea
            ref={textareaRef}
            value={code}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onSelect={handleSelectionChange}
            onPointerUp={handleSelectionChange}
            onPointerUpCapture={handleSelectionChange}
            onScroll={handleScroll}
            placeholder="# 欢迎使用R语言！
# 输入您的R代码，然后点击运行按钮或按Ctrl+Enter执行

# 快速开始：
data <- c(23, 45, 67, 89, 12, 34, 56, 78, 90, 43)
mean(data)
sd(data)
hist(data)"
            spellCheck={false}
          />
        </EditorContent>
      </EditorWrapper>
      <EditorHeader>
        <EditorButton
          variant="danger"
          onClick={() => setIsMethodSelectorOpen(true)}
        >
          <i className="fas fa-flask"></i>
          统计方法实验室
        </EditorButton>
        {aiReport && (
          <EditorButton
            variant="primary"
            onClick={() => setIsReportVisible(true)}
          >
            <i className="fas fa-file-alt"></i>
            查看实验报告
          </EditorButton>
        )}
      </EditorHeader>

      <MethodSelectorPanel
        isOpen={isMethodSelectorOpen}
        onClose={() => setIsMethodSelectorOpen(false)}
        onCodeGenerated={(code) => {
          console.log('Code generated:', code);
        }}
        onReportReceived={(report) => {
          setAiReport(report);
          setIsReportVisible(true);
          // Keep the spinner until the first streamed text arrives
          if (report) {
            setIsReportLoading(false);
          }
        }}
        onStreamingChange={(streaming) => {
          setIsReportStreaming(streaming);
          if (streaming) {
            setAiReport('');
            setIsReportVisible(true);
            setIsReportLoading(true);
          } else {
            setIsReportLoading(false);
          }
        }}
      />

      <AIReportPanel
        report={aiReport}
        isLoading={isReportLoading}
        isStreaming={isReportStreaming}
        visible={isReportVisible}
        onClose={() => setIsReportVisible(false)}
      />

      <AIHelpModal
        visible={isAIHelpOpen}
        onClose={() => setIsAIHelpOpen(false)}
        currentCode={code}
      />
    </EditorContainer>
  );
};